import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { KpiTrend } from './types';

interface KpiCardProps {
  value: number;
  unit?: string;
  trend?: KpiTrend;
  /** Pre-formatted override for the displayed value (e.g. `"7h 15m"`), bypassing the default number/percent formatting and the separate unit line. */
  displayValue?: string;
  /** Extra classes for the value text, e.g. to recolor it per metric. */
  valueClassName?: string;
}

/**
 * Scalar metric card for dashboard widgets — shows a large value, optional unit,
 * and a trend badge comparing the current period to the previous one.
 *
 * `unit === '%'` triggers percent formatting (one decimal, no separate unit label).
 * `trend.direction` can be `'up'`, `'down'`, `'neutral'`, or `'flat'` —
 * both `'neutral'` and `'flat'` render a dash icon with muted styling.
 *
 * @example
 * <KpiCard value={142} unit="tasks" trend={{ direction: 'up', value: 12.5, period: 'vs last month' }} />
 *
 * @example Pre-formatted value with a custom color
 * <KpiCard value={7.25} displayValue="7h 15m" valueClassName="text-cyan-600" />
 */
export function KpiCard({ value, unit, trend, displayValue, valueClassName }: KpiCardProps) {
  const formattedValue =
    displayValue ?? (unit === '%' ? `${value.toFixed(1)}%` : value.toLocaleString());

  const isUp = trend?.direction === 'up';
  const isDown = trend?.direction === 'down';

  const trendIcon = isUp ? (
    <TrendingUp className="h-3 w-3" />
  ) : isDown ? (
    <TrendingDown className="h-3 w-3" />
  ) : (
    <Minus className="h-3 w-3" />
  );

  const trendColor = isUp
    ? 'text-green-600 bg-green-50 dark:bg-green-950 dark:text-green-400'
    : isDown
      ? 'text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400'
      : 'text-muted-foreground bg-muted';

  const trendLabel = isUp
    ? `↑ ${Math.abs(trend!.value).toFixed(1)}%`
    : isDown
      ? `↓ ${Math.abs(trend!.value).toFixed(1)}%`
      : `— ${Math.abs(trend?.value ?? 0).toFixed(1)}%`;

  return (
    <div className="flex h-full flex-col justify-center gap-2 p-1">
      <p className={cn('text-4xl font-bold tracking-tight', valueClassName)}>{formattedValue}</p>
      {!displayValue && unit && unit !== '%' && (
        <p className="text-sm text-muted-foreground">{unit}</p>
      )}
      {trend && (
        <div className="flex items-center gap-1">
          <span
            className={cn(
              'flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
              trendColor,
            )}
          >
            {trendIcon}
            {trendLabel}
          </span>
          <span className="text-xs text-muted-foreground">{trend.period}</span>
        </div>
      )}
    </div>
  );
}
